import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Form,
  Modal,
  Select,
  Skeleton,
  Tag,
  Typography
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { TextInput } from "../../../components/Input/TextInput.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import {
  getContactConsent,
  recordConsent,
  recordInPersonMarketingConsent,
  requestMarketingSms
} from "../api/consent.ts"
import { useCommunicationRights } from "../communication-permissions.ts"
import { formatUsPhone } from "../settings/format-phone.ts"
import type { Channel, ContactRef } from "../types/compose.types.ts"
import {
  CHANNEL_REFUSAL,
  type ConsentState,
  type ContactConsent
} from "../types/consent.types.ts"

const CHANNELS: Channel[] = ["email", "sms"]

interface ChangeForm {
  state: ConsentState
  reason: string
  date: Dayjs
}

export const fieldErrors = (data: unknown) => {
  const body = (data ?? {}) as { details?: Record<string, unknown> } & Record<
    string,
    unknown
  >
  const details = (
    body.details && typeof body.details === "object" ? body.details : body
  ) as Record<string, unknown>
  return Object.entries(details)
    .filter(([, value]) => Array.isArray(value))
    .map(([name, value]) => ({ name, errors: value as string[] }))
}

export const ConsentCard = ({ contact }: { contact: ContactRef }) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { formatDate } = useDateFormatter()
  const rights = useCommunicationRights()
  const [form] = Form.useForm<ChangeForm>()
  const [consent, setConsent] = useState<ContactConsent | null>(null)
  const [failed, setFailed] = useState(false)
  const [changing, setChanging] = useState<Channel | null>(null)
  const [saving, setSaving] = useState(false)
  const [requesting, setRequesting] = useState(false)
  const [inPerson, setInPerson] = useState(false)
  const [customerTicked, setCustomerTicked] = useState(false)
  const [recordingInPerson, setRecordingInPerson] = useState(false)
  const companyName = useAppSelector(
    (state) => state.auth?.userInfo?.company_name
  )
  const requests = useRef(0)
  const contactKey = `${contact.type}:${contact.id}`

  const load = useCallback(async () => {
    const current = ++requests.current
    setFailed(false)
    const [type, id] = contactKey.split(":")
    const response = await getContactConsent({ type, id } as ContactRef)
    if (current !== requests.current) return
    if (!response.ok || !response.data) {
      setFailed(true)
      return
    }
    setConsent(response.data)
  }, [contactKey])

  useEffect(() => {
    void load()
  }, [load])

  const open = (channel: Channel) => {
    const standing = consent?.channels[channel].state ?? "allowed"
    form.setFieldsValue({
      state: standing === "allowed" ? CHANNEL_REFUSAL[channel] : "allowed",
      reason: "",
      date: dayjs()
    })
    setChanging(channel)
  }

  const save = async (values: ChangeForm) => {
    if (!changing) return
    setSaving(true)
    const response = await recordConsent(contact, {
      channel: changing,
      state: values.state,
      reason: values.reason.trim(),
      date: values.date.format("YYYY-MM-DD")
    })
    setSaving(false)
    if (!response.ok) {
      const errors = fieldErrors(response.data)
      form.setFields(
        errors.filter((error) =>
          ["state", "reason", "date"].includes(error.name)
        ) as never
      )
      if (!errors.length) message.error(t("communication.consent.saveFailed"))
      return
    }
    setConsent(unwrapEnvelope<ContactConsent>(response.data))
    setChanging(null)
    message.success(t("communication.consent.saved"))
  }

  const openInPerson = () => {
    setCustomerTicked(false)
    setInPerson(true)
  }

  const saveInPerson = async () => {
    setRecordingInPerson(true)
    const response = await recordInPersonMarketingConsent(contact)
    setRecordingInPerson(false)
    if (!response.ok) {
      const detail = (
        response.data as { details?: { detail?: unknown } } | undefined
      )?.details?.detail
      message.error(
        typeof detail === "string"
          ? detail
          : t("communication.consent.inPerson.failed")
      )
      return
    }
    setInPerson(false)
    message.success(t("communication.consent.inPerson.saved"))
    void load()
  }

  const askForMarketing = async () => {
    setRequesting(true)
    const response = await requestMarketingSms(contact)
    setRequesting(false)
    if (!response.ok) {
      const detail = (
        response.data as { details?: { detail?: unknown } } | undefined
      )?.details?.detail
      message.error(
        typeof detail === "string"
          ? detail
          : t("communication.consent.requestFailed")
      )
      return
    }
    const sent = unwrapEnvelope<{ status: string; reason: string }>(
      response.data
    )
    if (sent.status === "skipped") {
      message.warning(
        t(`communication.composer.reasons.${sent.reason}`, {
          defaultValue: t("communication.consent.requestFailed")
        })
      )
      return
    }
    message.success(t("communication.consent.requestSent"))
    void load()
  }

  const mayAskForMarketing = (channel: Channel) => {
    const state = consent?.channels[channel]
    return (
      channel === "sms" &&
      rights.send &&
      (contact.type === "customer" || contact.type === "lead") &&
      !!state?.address &&
      state.state === "allowed" &&
      !state.marketing_opt_in
    )
  }

  const mayAskByText = (channel: Channel) =>
    mayAskForMarketing(channel) &&
    !consent?.channels[channel].marketing_requested_at

  const stateTag = (state: ConsentState) => (
    <Tag color={state === "allowed" ? "green" : "red"}>
      {t(`communication.consent.state.${state}`)}
    </Tag>
  )

  const body = () => {
    if (failed)
      return (
        <Alert
          type="warning"
          showIcon
          message={t("communication.consent.loadFailed")}
          action={
            <Button size="small" onClick={load}>
              {t("communication.retry")}
            </Button>
          }
        />
      )
    if (!consent) return <Skeleton active paragraph={{ rows: 3 }} />
    return (
      <div className="flex flex-col gap-3">
        <ul
          aria-label={t("communication.consent.title")}
          className="m-0 flex list-none flex-col gap-3 p-0"
        >
          {CHANNELS.map((channel) => {
            const state = consent.channels[channel]
            return (
              <li
                key={channel}
                className="flex flex-wrap items-start justify-between gap-2"
              >
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-2">
                    <Typography.Text strong>
                      {t(`communication.templates.channel.${channel}`)}
                    </Typography.Text>
                    {stateTag(state.state)}
                  </span>
                  <Typography.Text type="secondary" className="text-xs">
                    {state.address
                      ? channel === "sms"
                        ? formatUsPhone(state.address)
                        : state.address
                      : t(`communication.composer.noAddress.${channel}`)}
                  </Typography.Text>
                  {state.state_changed_at && (
                    <Typography.Text type="secondary" className="text-xs">
                      {t("communication.consent.since", {
                        date: formatDate(state.state_changed_at),
                        source: t(
                          `communication.consent.source.${state.state_source}`,
                          {
                            defaultValue: state.state_source
                          }
                        )
                      })}
                    </Typography.Text>
                  )}
                  {channel === "sms" && (
                    <Typography.Text type="secondary" className="text-xs">
                      {state.marketing_opt_in
                        ? t("communication.consent.marketingYes")
                        : state.marketing_requested_at
                          ? t("communication.consent.marketingRequested", {
                              date: formatDate(state.marketing_requested_at)
                            })
                          : t("communication.consent.marketingNo")}
                    </Typography.Text>
                  )}
                </span>
                <span className="flex flex-wrap gap-2">
                  {mayAskByText(channel) && (
                    <Button
                      size="small"
                      loading={requesting}
                      onClick={askForMarketing}
                    >
                      {t("communication.consent.requestMarketing")}
                    </Button>
                  )}
                  {mayAskForMarketing(channel) && (
                    <Button size="small" onClick={openInPerson}>
                      {t("communication.consent.inPerson.open")}
                    </Button>
                  )}
                  {rights.send && (
                    <Button size="small" onClick={() => open(channel)}>
                      {t("communication.consent.change", {
                        channel: t(`communication.templates.channel.${channel}`)
                      })}
                    </Button>
                  )}
                </span>
              </li>
            )
          })}
        </ul>
        {consent.history.length > 0 && (
          <Collapse
            ghost
            size="small"
            items={[
              {
                key: "history",
                label: t("communication.consent.history", {
                  count: consent.history.length
                }),
                children: (
                  <ol className="m-0 flex flex-col gap-2 pl-5">
                    {consent.history.map((event) => (
                      <li key={event.id}>
                        <Typography.Text>
                          {t("communication.consent.event", {
                            channel: t(
                              `communication.templates.channel.${event.channel}`
                            ),
                            from: t(
                              `communication.consent.state.${event.from_value}`,
                              {
                                defaultValue: event.from_value
                              }
                            ),
                            to: t(
                              `communication.consent.state.${event.to_value}`,
                              {
                                defaultValue: event.to_value
                              }
                            )
                          })}
                        </Typography.Text>
                        <br />
                        <Typography.Text type="secondary" className="text-xs">
                          {[
                            formatDate(event.occurred_at),
                            event.recorded_by_name ??
                              t(
                                `communication.consent.source.${event.source}`,
                                {
                                  defaultValue: event.source
                                }
                              ),
                            event.reason
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography.Text>
                      </li>
                    ))}
                  </ol>
                )
              }
            ]}
          />
        )}
      </div>
    )
  }

  return (
    <Card size="small" title={t("communication.consent.title")}>
      {body()}
      <Modal
        open={changing !== null}
        title={
          changing
            ? t("communication.consent.change", {
                channel: t(`communication.templates.channel.${changing}`)
              })
            : undefined
        }
        okText={t("communication.consent.record")}
        cancelText={t("communication.bulk.cancel")}
        confirmLoading={saving}
        onOk={() => form.submit()}
        onCancel={() => setChanging(null)}
        destroyOnHidden
      >
        <Form<ChangeForm>
          form={form}
          layout="vertical"
          onFinish={save}
          preserve={false}
        >
          <Form.Item name="state" label={t("communication.consent.newState")}>
            <Select
              aria-label={t("communication.consent.newState")}
              options={
                changing
                  ? (
                      ["allowed", CHANNEL_REFUSAL[changing]] as ConsentState[]
                    ).map((value) => ({
                      value,
                      label: t(`communication.consent.state.${value}`)
                    }))
                  : []
              }
            />
          </Form.Item>
          <Form.Item
            name="reason"
            label={t("communication.consent.reason")}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("communication.consent.reasonRequired")
              }
            ]}
          >
            <TextInput
              placeholder={t("communication.consent.reasonPlaceholder")}
              maxLength={255}
            />
          </Form.Item>
          <Form.Item
            name="date"
            label={t("communication.consent.date")}
            rules={[
              {
                required: true,
                message: t("communication.consent.dateRequired")
              }
            ]}
          >
            <DatePicker
              aria-label={t("communication.consent.date")}
              className="w-full"
              disabledDate={(day) => day.isAfter(dayjs(), "day")}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={inPerson}
        title={t("communication.consent.inPerson.title")}
        okText={t("communication.consent.inPerson.save")}
        cancelText={t("communication.bulk.cancel")}
        okButtonProps={{ disabled: !customerTicked }}
        confirmLoading={recordingInPerson}
        onOk={saveInPerson}
        onCancel={() => setInPerson(false)}
        destroyOnHidden
      >
        <Typography.Paragraph type="secondary">
          {t("communication.consent.inPerson.handOver")}
        </Typography.Paragraph>
        <Checkbox
          checked={customerTicked}
          onChange={(event) => setCustomerTicked(event.target.checked)}
        >
          {t("communication.consent.inPerson.offer", {
            company:
              companyName || t("communication.consent.inPerson.yourContractor")
          })}
        </Checkbox>
      </Modal>
    </Card>
  )
}
